<template>
  <v-card class="ma-0 pa-2 card" color="background" bg-color="background" variant="elevated" elevation="4"
    density="compact">
    <v-form class="ma-0 pa-0 card-content" bg-color="background" @submit.prevent density="compact" autocomplete="off">
      <v-card-item v-if="!show" class="voice-summary" @click="show = true">
        <template #prepend>
          <v-avatar size="40" rounded="lg" color="primary" class="mr-3">
            <span class="voice-summary-initials">{{ voiceInitials }}</span>
          </v-avatar>
        </template>
        <v-card-title class="text-truncate">{{ name || 'New Voice' }}</v-card-title>
        <v-card-subtitle class="text-truncate">
          {{ providerKey || 'No provider' }} · {{ selectedVoiceName }}
        </v-card-subtitle>
      </v-card-item>
      <v-card-text v-if="!show" class="pt-0 pb-2 voice-summary-text">
        <div class="d-flex flex-wrap align-center">
          <v-chip v-for="[key, value] in selectedVoiceLabelsEntries.slice(0, 3)" :key="`${key}-${value}`" size="small"
            variant="tonal" class="mr-2 mb-2">
            {{ value }}
          </v-chip>
          <v-chip v-if="responseFormat" size="small" variant="outlined" class="mr-2 mb-2">
            {{ responseFormat }}
          </v-chip>
        </div>
      </v-card-text>
      <v-expand-transition>
        <v-list class="ma-0 pa-0 card-content" bg-color="background" v-show="show" density="compact">
          <!-- Provider Selection (audio-capable only) -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Select an audio-capable provider for this voice.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-select v-model="providerKey" @update:modelValue="providerChanged" :items="audioProviderKeys"
              label="Provider" class="pt-2" variant="outlined" density="compact"
              :rules="[() => !!providerKey || 'Provider is required']" :readonly="!isCopy" />
          </v-list-item>

          <!-- Voice Selection (from provider inventory) -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Select a voice from the provider's available voices. Use Sync to refresh the list.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-autocomplete v-model="selectedVoiceId" @update:modelValue="voiceSelected" :items="voices"
              item-title="name" item-value="id" label="Provider Voices" class="pt-2" variant="outlined"
              density="compact" :disabled="!providerKey" :loading="voicesLoading" :readonly="voicesLoading"
              :rules="[() => !!selectedVoiceId || 'Provider voice is required']">
              <template v-slot:append>
                <Button color="primary" buttonClass="ml-n2" :click="syncVoices" :disabled="syncDisabled" icon="mdi-sync"
                  size="x-large" tooltip="Sync Voices" />
              </template>
              <template #item="{ props, item }">
                <v-list-item v-bind="props" :title="undefined">
                  <v-list-item-title>
                    <span>{{ item.raw.name ?? item.raw.id }}</span>
                    <v-chip v-if="item.raw.labels?.descriptive" size="x-small" class="ml-2" color="warning">
                      {{ item.raw.labels.descriptive }}
                    </v-chip>
                    <v-chip v-if="item.raw.labels?.accent" size="x-small" class="ml-2"
                      :color="accentChipColor(item.raw.labels.accent)">
                      {{ item.raw.labels.accent }}
                    </v-chip>
                    <v-chip v-if="item.raw.labels?.gender" size="x-small" class="ml-2"
                      :color="genderChipColor(item.raw.labels.gender)">
                      {{ item.raw.labels.gender }}
                    </v-chip>
                  </v-list-item-title>
                </v-list-item>
              </template>
              <template #selection="{ item }">
                <span>{{ item?.raw?.name ?? item?.raw?.id }}</span>
              </template>
            </v-autocomplete>
          </v-list-item>

          <!-- Alias Name -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Alias name must be unique and will appear in voice lists.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-text-field v-model="name" label="Voice Alias" class="pt-2" :rules="nameRules" variant="outlined"
              density="compact" :readonly="!isCopy" :clearable="isCopy" />
          </v-list-item>

          <!-- Description (optional) -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Optional description of the voice.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-text-field v-model="description" label="Description" class="pt-2" variant="outlined" density="compact" />
          </v-list-item>

          <!-- TTS: Response Format (optional) -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Optional default audio format for this voice. Leave blank to use server default (mp3).">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-select v-model="responseFormat" :items="responseFormatItems" label="Response Format (optional)"
              class="pt-2" variant="outlined" density="compact" clearable />
          </v-list-item>

          <!-- TTS: Speed (optional) -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Optional default playback speed. Range 0.25–4.0.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-3 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <div class="w-100">
              <v-row class="ma-0 pa-0" dense>
                <v-col cols="12" class="ma-0 pr-2">
                  <v-slider class="voice-slider ml-0" v-model="speedField" color="primary" track-color="secondary"
                    :min="0.25" :max="4.0" :step="0.05" :track-size="6" :thumb-size="18" density="compact">
                    <template #prepend>
                      <span class="text-caption">Speed</span>
                    </template>
                    <template #append>
                      <span>{{ Number.isFinite(Number(speedField)) ? Number(speedField).toFixed(2) : '1' }}</span>
                    </template>
                  </v-slider>
                </v-col>
              </v-row>
            </div>
          </v-list-item>

          <!-- ElevenLabs Options (optional; shown only for ElevenLabs) -->
          <v-list-item v-if="isElevenLabs" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'"
            density="compact">
            <v-tooltip v-if="showGlobalTooltips" position="top" max-width="300"
              text="Optional ElevenLabs voice settings.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small"
                  class="global-tooltip-icon mt-2 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <div class="w-100">
              <v-row class="ma-0 pa-0" dense>
                <v-col cols="6" class="ma-0 pa-0">
                  <v-slider class="voice-slider ml-0" v-model="ttsStabilityField" color="primary"
                    track-color="secondary" :min="0" :max="1" :step="0.05" :track-size="6" :thumb-size="18"
                    density="compact">
                    <template #prepend>
                      <span class="text-caption">Stability</span>
                    </template>
                    <template #append>
                      <span>{{ Number.isFinite(Number(ttsStabilityField)) ? Number(ttsStabilityField).toFixed(2) : '—'
                        }}</span>
                    </template>
                  </v-slider>
                </v-col>
                <v-col cols="6" class="ma-0 pa-0">
                  <v-slider class="voice-slider" v-model="ttsSimilarityBoostField" color="primary"
                    track-color="secondary" :min="0" :max="1" :step="0.05" :track-size="6" :thumb-size="18"
                    density="compact">
                    <template #prepend>
                      <span class="text-caption">Similarity<br />Boost</span>
                    </template>
                    <template #append>
                      <span class="slider-thumb-label">{{ Number.isFinite(Number(ttsSimilarityBoostField)) ?
                        Number(ttsSimilarityBoostField).toFixed(2) : '—' }}</span>
                    </template>
                  </v-slider>
                </v-col>
                <v-col cols="6" class="ma-0 pa-0">
                  <v-slider class="voice-slider ml-0" v-model="ttsStyleField" color="primary" track-color="secondary"
                    :min="0" :max="1" :step="0.05" :track-size="6" :thumb-size="18" density="compact">
                    <template #prepend>
                      <span class="text-caption">Style</span>
                    </template>
                    <template #append>
                      <span class="slider-thumb-label">{{ Number.isFinite(Number(ttsStyleField)) ?
                        Number(ttsStyleField).toFixed(2) : '—' }}</span>
                    </template>
                  </v-slider>
                </v-col>
                <v-col cols="6" class="ma-0 pa-0">
                  <v-switch class="ml-2" v-model="ttsUseSpeakerBoostField" color="primary" base-color="primary"
                    density="compact" inset>
                    <template #prepend>
                      <span class="text-caption">Use Speaker Boost</span>
                    </template>
                  </v-switch>
                </v-col>
              </v-row>
            </div>
          </v-list-item>

          <!-- Provider Voice Labels (informational) -->
          <v-list-item v-if="selectedVoiceLabelsEntries.length > 0"
            :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <div class="pt-0">
              <div class="text-caption mb-1">Voice Labels</div>
              <span v-for="([k,v], idx) in selectedVoiceLabelsEntries" :key="k + idx">
                <v-chip size="x-small" class="mr-2 mb-1" color="success">{{ k }}: {{ v }}</v-chip>
              </span>
            </div>
          </v-list-item>

          <!-- Test Synthesis -->
          <v-list-item :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-text-field v-model="testText" label="Try a short phrase to test this voice" class="pt-2"
              variant="outlined" density="compact" clearable @keydown.enter.prevent="testVoice">
              <template #append>
                <v-btn :disabled="!canTest" icon variant="text" density="compact" size="x-large" color="secondary"
                  class="ml-0 mr-1" @click="testVoice">
                  <v-icon icon="mdi-chevron-right"></v-icon>
                </v-btn>
                <v-btn v-if="hasTestAudio" icon variant="text" density="compact" size="x-large" color="warning"
                  class="ml-0" @click="clearTest">
                  <v-icon icon="mdi-close"></v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-list-item>

          <!-- Preview (provider preview or test audio if present) -->
          <v-list-item v-if="finalPreviewUrl" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'"
            density="compact">
            <AudioPlayer :src="finalPreviewUrl" :canv-height="40" :canv-width="300" :bar-color="audioColor"
              :show-controls="true" :auto-play="hasTestAudio" />
          </v-list-item>
        </v-list>
      </v-expand-transition>

      <v-card-actions density="compact">
        <Button v-if="show" color="primary" class="mr-4" :disabled="saveDisabled" :click="updateVoice"
          icon="mdi-check-bold" size="x-large" tooltip="Save Voice" density="compact" />
        <Button v-if="show && !isCopy" color="primary" class="mr-4" :click="initiateDeleteVoice"
          icon="mdi-trash-can-outline" size="x-large" tooltip="Delete Voice" density="compact" />
        <v-spacer />
        <v-btn color="primary" class="mx-auto" :icon="show ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          @click="show = !show" density="compact" />
      </v-card-actions>
    </v-form>

    <div v-if="waiting" class="card-action-overlay">
      <h2>{{ actionMessage }}</h2>
    </div>

    <ConfirmationDialog v-model="showDeleteConfirmationDialog" title="Confirm Voice Deletion"
      message="Are you sure you want to delete this voice?" :itemName="name" itemType="voice" :loading="waiting"
      @confirm="confirmVoiceDelete" @cancel="cancelDeleteVoice" />
  </v-card>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import Button from '@/components/sub/Button.vue'
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue'
import AudioPlayer from '@/components/sub/AudioPlayer.vue'
import type { ProviderKey, ProviderConfig } from '@/types/providers'
import type { ProviderInventoryItem, ProviderVoice } from '@/types/msq'
import type { VoiceConfig } from '@/types/voices'
import { synthesizeSpeech } from '@/utils/api'

@Options({
  props: {
    voiceId: String, // internal ID for existing saved voice (UUID). If absent, new card.
    isOverlay: Boolean,
    startExpanded: Boolean
  },
  components: {
    Button,
    ConfirmationDialog,
    AudioPlayer
  },
  computed: {
    ...mapGetters([
      'providerConfigs', 'providerKeys', 'allProviderModels', 'showGlobalTooltips',
      'voiceConfigs', 'voiceConfigsById', 'voiceConfigById'
    ])
  },
  emits: ['remove-card', 'saved']
})
export default class VoiceCard extends Vue {
  // Props
  public voiceId!: string | undefined
  public isOverlay!: boolean
  public startExpanded!: boolean

  // Store-bound getters
  public providerConfigs!: Record<string, ProviderConfig>
  public providerKeys!: string[]
  public allProviderModels!: Record<string, ProviderInventoryItem[]>
  public showGlobalTooltips!: boolean
  public voiceConfigs!: Record<string, VoiceConfig>
  public voiceConfigsById!: Record<string, VoiceConfig>
  public voiceConfigById!: (id: string) => VoiceConfig | undefined

  // UI state
  private show_ = true
  public waiting = false
  public actionMessage = ''
  public voicesLoading = false
  public syncDisabled = false
  public saveDisabled = true
  public isCopy_ = true

  // Fields
  private name_ = ''
  private description_ = ''
  private providerKey_: ProviderKey | null = null
  private selectedVoiceId_: string = ''
  private responseFormat_: 'mp3' | 'opus' | 'aac' | 'flac' | 'wav' | 'pcm' | null = null
  private speed_: number | null = null
  private ttsStability_: number | null = null
  private ttsSimilarityBoost_: number | null = null
  private ttsStyle_: number | null = null
  private ttsUseSpeakerBoost_: boolean | null = null

  // Test synthesis fields
  private testText_: string = ''
  private testAudioUrl_: string | null = null

  // Delete dialog
  public showDeleteConfirmationDialog = false

  // Computed getters/setters
  get show(): boolean { return this.show_ }
  set show(v: boolean) { this.show_ = v }

  get isCopy(): boolean { return this.isCopy_ }
  set isCopy(v: boolean) { this.isCopy_ = v }

  get name(): string { return this.name_ }
  set name(v: string) { this.name_ = v; this.checkHash() }

  get description(): string { return this.description_ }
  set description(v: string) { this.description_ = v; this.checkHash() }

  get providerKey(): ProviderKey | null { return this.providerKey_ }
  set providerKey(v: ProviderKey | null) { this.providerKey_ = v; this.checkHash() }

  get selectedVoiceId(): string { return this.selectedVoiceId_ }
  set selectedVoiceId(v: string) { this.selectedVoiceId_ = v; this.checkHash() }

  get responseFormat(): 'mp3' | 'opus' | 'aac' | 'flac' | 'wav' | 'pcm' | null { return this.responseFormat_ }
  set responseFormat(v: 'mp3' | 'opus' | 'aac' | 'flac' | 'wav' | 'pcm' | null) { this.responseFormat_ = v; this.checkHash() }

  get speed(): number | null { return this.speed_ }
  set speed(v: number | null) { this.speed_ = v; this.checkHash() }

  get ttsStability(): number | null { return this.ttsStability_ }
  set ttsStability(v: number | null) { this.ttsStability_ = v; this.checkHash() }

  get ttsSimilarityBoost(): number | null { return this.ttsSimilarityBoost_ }
  set ttsSimilarityBoost(v: number | null) { this.ttsSimilarityBoost_ = v; this.checkHash() }

  get ttsStyle(): number | null { return this.ttsStyle_ }
  set ttsStyle(v: number | null) { this.ttsStyle_ = v; this.checkHash() }

  get ttsUseSpeakerBoost(): boolean | null { return this.ttsUseSpeakerBoost_ }
  set ttsUseSpeakerBoost(v: boolean | null) { this.ttsUseSpeakerBoost_ = v; this.checkHash() }

  // UI proxy fields to avoid null being bound to components expecting number|undefined/boolean|undefined
  get speedField(): number | undefined { return this.speed_ ?? undefined }
  set speedField(v: number | undefined) { this.speed_ = typeof v === 'number' ? v : null; this.checkHash() }

  get ttsStabilityField(): number | undefined { return this.ttsStability_ ?? undefined }
  set ttsStabilityField(v: number | undefined) { this.ttsStability_ = typeof v === 'number' ? v : null; this.checkHash() }

  get ttsSimilarityBoostField(): number | undefined { return this.ttsSimilarityBoost_ ?? undefined }
  set ttsSimilarityBoostField(v: number | undefined) { this.ttsSimilarityBoost_ = typeof v === 'number' ? v : null; this.checkHash() }

  get ttsStyleField(): number | undefined { return this.ttsStyle_ ?? undefined }
  set ttsStyleField(v: number | undefined) { this.ttsStyle_ = typeof v === 'number' ? v : null; this.checkHash() }

  get ttsUseSpeakerBoostField(): boolean | undefined { return this.ttsUseSpeakerBoost_ ?? undefined }
  set ttsUseSpeakerBoostField(v: boolean | undefined) { this.ttsUseSpeakerBoost_ = typeof v === 'boolean' ? v : null; this.checkHash() }

  // Validation
  get nameRules() {
    return [
      (v: string) => !!v || 'Alias is required'
    ]
  }

  // Only providers that have 'audio' capability
  get audioProviderKeys(): string[] {
    const keys = this.providerKeys || []
    return keys.filter(k => {
      const cap = this.providerConfigs?.[k]?.capabilities
      return Array.isArray(cap) ? cap.includes('audio') : false
    })
  }

  // Inventory voices for selected provider
get voices(): ProviderVoice[] {
  if (!this.providerKey) return []
  const list = this.allProviderModels[this.providerKey] || []
  const voiceItems = list.filter((i): i is ProviderVoice => (i as any).object === 'voice')
  return voiceItems
    .sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id))
}

  // Labels + preview from selected voice (informational)
  get selectedVoiceLabelsEntries(): Array<[string, string]> {
    const v = this.voices.find(x => x.id === this.selectedVoiceId)
    const labels = v?.labels || {}
    return Object.entries(labels)
  }

  get selectedPreviewUrl(): string | null {
    const v = this.voices.find(x => x.id === this.selectedVoiceId)
    return v?.previewUrl || null
  }

  get selectedVoiceName(): string {
    const v = this.voices.find(x => x.id === this.selectedVoiceId)
    return v?.name || v?.id || 'No voice selected'
  }

  get voiceInitials(): string {
    const raw = (this.name || this.selectedVoiceName || 'VC').replace(/[^a-zA-Z0-9]/g, '')
    return raw.slice(0, 2).toUpperCase() || 'VC'
  }

  // If a test audio has been generated, prefer it over provider preview
  get finalPreviewUrl(): string | null {
    return this.testAudioUrl_ || this.selectedPreviewUrl
  }

  get testText(): string { return this.testText_ }
  set testText(v: string) { this.testText_ = v }

  get hasTestAudio(): boolean { return !!this.testAudioUrl_ }
  get canTest(): boolean {
    const hasText = (this.testText_?.trim().length ?? 0) > 0
    const canViaSaved = !this.isCopy && !!this.name
    const canViaSelection = !!this.providerKey && !!this.selectedVoiceId
    return hasText && (canViaSaved || canViaSelection)
  }

  // Vuetify theme-backed color (reuse default primary if needed)
  get audioColor(): string {
    return '#6200EE'
  }

  // Chip color helpers (mirror ProviderCard)
  genderChipColor(g?: string): string | undefined {
    if (!g) return undefined
    const v = g.toLowerCase()
    if (v === 'male') return 'primary'
    if (v === 'female') return 'secondary'
    return undefined
  }

  accentChipColor(a?: string): string | undefined {
    if (!a) return undefined
    const v = a.toLowerCase()
    if (v === 'american') return 'primary'
    if (v === 'australian') return 'secondary'
    if (v === 'british') return 'warning'
    return undefined
  }

  // Provider helpers
  get isElevenLabs(): boolean {
    return typeof this.providerKey === 'string' && String(this.providerKey).toLowerCase() === 'elevenlabs'
  }
  get responseFormatItems(): Array<'mp3' | 'opus' | 'aac' | 'flac' | 'wav' | 'pcm'> {
    return ['mp3', 'opus', 'aac', 'flac', 'wav', 'pcm']
  }

  clampSpeed(val: number | null): number | null {
    if (val == null || Number.isNaN(val as any)) return null
    const s = Math.max(0.25, Math.min(4.0, Number(val)))
    return Number.isFinite(s) ? s : null
  }

  created() {
    this.initializeData()
    if (!this.providerConfigs || Object.keys(this.providerConfigs).length === 0) {
      this.$store.dispatch('fetchProviders')
    }
  }

  initializeData() {
    if (this.voiceId) {
      const cfg = this.voiceConfigsById?.[this.voiceId]
      if (cfg) {
        this.isCopy = false
        this.show_ = !!this.startExpanded
        this.name_ = cfg.name
        this.description_ = cfg.description || ''
        this.providerKey_ = cfg.providerKey
        this.selectedVoiceId_ = cfg.voiceId
        // Seed optional TTS defaults if present
        this.responseFormat_ = (cfg as any).responseFormat ?? null
        this.speed_ = (cfg as any).speed ?? null
        const opts = (cfg as any).ttsOptions as Record<string, unknown> | undefined
        if (opts) {
          this.ttsStability_ = typeof opts['stability'] === 'number' ? (opts['stability'] as number) : null
          this.ttsSimilarityBoost_ = typeof opts['similarityBoost'] === 'number' ? (opts['similarityBoost'] as number) : null
          this.ttsStyle_ = typeof opts['style'] === 'number' ? (opts['style'] as number) : null
          this.ttsUseSpeakerBoost_ = typeof opts['useSpeakerBoost'] === 'boolean' ? (opts['useSpeakerBoost'] as boolean) : null
        }
        this.syncDisabled = false
        this.saveDisabled = true // unchanged unless edits occur
        return
      }
    }
    // New card
    this.isCopy = true
    this.show_ = true
    this.saveDisabled = true
  }

  providerChanged() {
    this.selectedVoiceId = ''
  }

  voiceSelected(_selectedId: string) {
    // Auto-fill alias from provider name/id if empty
    if (this.isCopy && (this.name ?? '').trim() === '') {
      const v = this.voices.find(x => x.id === _selectedId)
      if (v) this.name = v.name || v.id
    }
    // If description is empty, fill from labels in priority order: descriptive, accent, use_case, gender
    if ((this.description ?? '').trim() === '') {
      const v = this.voices.find(x => x.id === _selectedId)
      const labels = v?.labels as Record<string, string> | undefined
      if (labels) {
        const order = ['descriptive', 'accent', 'use_case', 'gender']
        for (const key of order) {
          const val = labels[key]
          if (val && String(val).trim() !== '') {
            this.description = String(val)
            break
          }
        }
      }
    }
  }

  checkHash() {
    const required = !!this.name && !!this.providerKey && !!this.selectedVoiceId
    this.saveDisabled = !required
  }

  async syncVoices() {
    if (!this.providerKey) {
      this.$store.dispatch('updateSnackbar', { text: 'Please select a provider first.', timeout: 3000 })
      return
    }
    this.voicesLoading = true
    this.syncDisabled = true
    await this.$store.dispatch('fetchProviderModels', { providerConfig: { providerKey: this.providerKey } })
    this.voicesLoading = false
    this.syncDisabled = false
  }

  async testVoice() {
    if (!this.canTest) {
      this.$store.dispatch('updateSnackbar', { text: 'Enter text and select a voice first.', timeout: 3000 })
      return
    }
    // Build request payload; prefer saved voice alias if not a new card
    const payload: any = {
      text: this.testText.trim()
    }
    if (!this.isCopy && this.name) {
      payload.voiceName = this.name
    } else {
      payload.providerKey = this.providerKey
      payload.voice = this.selectedVoiceId
    }
    // Include optional overrides if set
    if (this.responseFormat) payload.responseFormat = this.responseFormat
    if (typeof this.speed === 'number') payload.speed = this.speed
    if (this.isElevenLabs) {
      const tts: Record<string, unknown> = {}
      if (typeof this.ttsStability === 'number') tts.stability = this.ttsStability
      if (typeof this.ttsSimilarityBoost === 'number') tts.similarityBoost = this.ttsSimilarityBoost
      if (typeof this.ttsStyle === 'number') tts.style = this.ttsStyle
      if (typeof this.ttsUseSpeakerBoost === 'boolean') tts.useSpeakerBoost = this.ttsUseSpeakerBoost
      if (Object.keys(tts).length > 0) payload.ttsOptions = tts
    }

    // Use card overlay for quick feedback
    this.waiting = true
    this.actionMessage = 'Generating preview...'
    try {
      const resp: any = await synthesizeSpeech(payload)
      if (resp && resp.error) {
        this.$store.dispatch('updateSnackbar', { text: `TTS Error: ${resp.message}`, timeout: 4000 })
        this.$store.dispatch('addLogEvent', { title: 'TTS Error', content: resp }, { root: true })
        return
      }
      // Replace preview with the synthesized audio
      if (this.testAudioUrl_) {
        try { URL.revokeObjectURL(this.testAudioUrl_) } catch (e) { /* ignore */ void 0 }
      }
      const mime = resp?.contentType || 'audio/mpeg'
      const blob = new Blob([resp.buffer], { type: mime })
      this.testAudioUrl_ = URL.createObjectURL(blob)
      this.$store.dispatch('addLogEvent', { title: 'TTS Preview Generated', content: { mime, bytes: blob.size } }, { root: true })
    } catch (e: any) {
      this.$store.dispatch('updateSnackbar', { text: `TTS failed: ${e?.message || String(e)}`, timeout: 4000 })
      this.$store.dispatch('addLogEvent', { title: 'TTS Exception', content: { error: e } }, { root: true })
    } finally {
      this.waiting = false
      this.actionMessage = ''
    }
  }

  clearTest() {
    if (this.testAudioUrl_) {
      try { URL.revokeObjectURL(this.testAudioUrl_) } catch (e) { /* ignore */ void 0 }
      this.testAudioUrl_ = null
    }
  }

  async updateVoice() {
    if (this.saveDisabled || !this.providerKey || !this.selectedVoiceId || !this.name) {
      this.$store.dispatch('updateSnackbar', { text: 'Cannot save: Missing required fields.', timeout: 3000 })
      return
    }
    // Validate/clamp speed if provided
    if (this.speed != null) {
      const clamped = this.clampSpeed(this.speed)
      if (clamped == null || clamped < 0.25 || clamped > 4.0) {
        this.$store.dispatch('updateSnackbar', { text: 'Speed must be between 0.25 and 4.0', timeout: 3000 })
        return
      }
      this.speed = clamped
    }

    this.waiting = true
    this.actionMessage = 'Saving...'

    const payload: any = {
      name: this.name,
      providerKey: this.providerKey,
      voiceId: this.selectedVoiceId,
      description: (this.description?.trim() || undefined),
      overwrite: this.isCopy
    }
    if (this.responseFormat) payload.responseFormat = this.responseFormat
    if (typeof this.speed === 'number') payload.speed = this.speed

    if (this.isElevenLabs) {
      const tts: Record<string, unknown> = {}
      if (typeof this.ttsStability === 'number') tts.stability = this.ttsStability
      if (typeof this.ttsSimilarityBoost === 'number') tts.similarityBoost = this.ttsSimilarityBoost
      if (typeof this.ttsStyle === 'number') tts.style = this.ttsStyle
      if (typeof this.ttsUseSpeakerBoost === 'boolean') tts.useSpeakerBoost = this.ttsUseSpeakerBoost
      if (Object.keys(tts).length > 0) payload.ttsOptions = tts
    }

    const success = await this.$store.dispatch('setVoiceConfig', payload)
    this.waiting = false
    this.actionMessage = ''
    if (success) {
      this.isCopy = false
      this.$emit('saved', { name: this.name })
    }
  }

  initiateDeleteVoice() {
    if (this.isCopy) {
      this.$emit('remove-card')
      return
    }
    if (this.name) {
      this.showDeleteConfirmationDialog = true
    }
  }

  async confirmVoiceDelete() {
    if (this.name) {
      this.waiting = true
      this.actionMessage = 'Deleting...'
      await this.$store.dispatch('deleteVoice', this.name)
      this.waiting = false
      this.actionMessage = ''
    }
    this.cancelDeleteVoice()
  }

  cancelDeleteVoice() {
    this.showDeleteConfirmationDialog = false
  }

  // Cleanup any object URLs created for preview
  beforeUnmount() {
    if (this.testAudioUrl_) {
      try { URL.revokeObjectURL(this.testAudioUrl_) } catch (e) { /* ignore */ void 0 }
      this.testAudioUrl_ = null
    }
  }
}
</script>

<style scoped lang="scss">
.card {
  border: 1px solid rgba(255, 255, 255, 0.3);
}
.voice-summary {
  cursor: pointer;
}
.voice-summary-initials {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.voice-summary-text {
  padding-left: 72px;
}
.card-action-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  border-radius: inherit;
}
.max-85 {
  max-width: 85%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.global-tooltip-icon {
  color: rgba(var(--v-theme-on-surface), 0.6);
}

/* Voice slider theming with app theme colors */
// :deep(.voice-slider .v-slider-track__background) {
//   background-color: rgba(var(--v-theme-border-color), 0.5);
// }
// :deep(.voice-slider .v-slider-track__fill) {
//   background-color: rgb(var(--v-theme-primary));
// }
// :deep(.voice-slider .v-slider-thumb) {
//   border: 2px solid rgb(var(--v-theme-primary));
//   background-color: rgb(var(--v-theme-surface));
// }
// .slider-thumb-label {
//   background-color: rgb(var(--v-theme-surface-variant));
//   color: rgb(var(--v-theme-on-surface));
//   border: 1px solid rgb(var(--v-theme-border-color));
//   padding: 2px 6px;
//   border-radius: 6px;
//   font-size: 12px;
// }
</style>
